
/**
 * ValuationInput Component
 * Created: 2025-05-24 - Added to ensure ValuationForm renders properly
 * Updated: 2025-07-23 - Added loading state and improved UX
 * Updated: 2025-09-20 - Complete redesign to match new hero section layout
 * Updated: 2025-09-28 - Complete overhaul with new card design, proper typography, and exact styling specifications; Fixed input container padding to match reference design
 * Updated: 2025-09-28 - Restructured layout to flatten nested containers, standardized all input heights to h-14, and improved spacing with uniform gap-5 between inputs and larger gap before button
 * Updated: 2025-09-28 - Fixed button width to match input containers by changing from inline-flex to flex and centering button content; Removed black rectangle from transmission dropdown
 * Updated: 2025-09-28 - Fixed arrow issues: removed custom SVG arrows from mileage and transmission inputs to use native browser arrows, and removed button padding to ensure full-width matching
 * Updated: 2025-09-28 - Translated form text to Polish: VIN placeholder to "Numer VIN", mileage to "Przebieg", transmission options to "Manualna"/"Automatyczna", button to "Otrzymaj wycenę!"
 * Updated: 2025-10-01 - Updated placeholders to "Wprowadź numer VIN" and "Wprowadź przebieg"; Removed number input spinner arrows from mileage field; Changed transmission placeholder to "Wybierz skrzynie biegów"; Set all placeholders to Heebo Medium 500
 * Updated: 2025-10-01 - Added mobile optimization with responsive sizing and touch-friendly targets
 * Updated: 2025-10-02 - Fixed vertical centering of placeholder text in all input containers; Ensured consistent font-weight across all placeholders
 * Updated: 2025-10-02 - Explicitly set all placeholders to font-weight 500 (font-[500]) to ensure identical thickness across all form inputs
 * Updated: 2025-10-02 - Reduced transmission select font-weight to 400 to visually match other placeholders (Select component renders differently than inputs)
 * Updated: 2025-10-03 - Added tablet support with isTablet prop for iPad-optimized sizing between mobile and desktop
 * Updated: 2025-10-05 - Fixed VIN input width from w-32 to flex-1 to ensure entire 17-character VIN is visible when typing
 * Updated: 2025-11-09 - Added waitingForVerification prop to separate security verification from form submission loading
 */

import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface ValuationInputProps {
  form: UseFormReturn<any>;
  isLoading?: boolean;
  waitingForVerification?: boolean;
  isMobile?: boolean;
  isTablet?: boolean;
}

const ValuationInput = ({ form, isLoading = false, waitingForVerification = false, isMobile = false, isTablet = false }: ValuationInputProps) => {
  // Responsive sizing based on device
  const inputHeight = isMobile ? "h-12" : isTablet ? "h-13" : "h-14";
  const inputPadding = isMobile ? "pl-4 pr-4 py-3" : isTablet ? "pl-6 pr-5 py-3.5" : "pl-8 pr-6 py-4";
  const fontSize = isMobile ? "text-sm" : isTablet ? "text-base" : "text-base";
  const buttonPadding = isMobile ? "pt-3 pb-3" : isTablet ? "pt-4 pb-4" : "pt-5 pb-5";
  const buttonText = isMobile ? "text-lg" : isTablet ? "text-xl" : "text-2xl";
  const containerGap = isMobile ? "gap-4" : isTablet ? "gap-5" : "gap-5";
  const buttonMargin = isMobile ? "mt-2" : isTablet ? "mt-2.5" : "mt-3";

  return (
    <div data-layer="Input Container" className={`InputContainer self-stretch flex flex-col justify-start items-start ${containerGap}`}>
      {/* VIN Input */}
      <div className="self-stretch flex flex-col gap-1">
        <div data-layer="Input Container" className={`InputContainer self-stretch ${inputHeight} ${inputPadding} bg-neutral-50 rounded-lg flex items-center overflow-hidden`}>
          <Input
            type="text"
            placeholder="Wprowadź numer VIN"
            {...form.register('vin')}
            className={`flex-1 justify-start text-zinc-700 ${fontSize} font-medium font-['Heebo'] border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto placeholder:text-zinc-700 placeholder:font-[500]`}
            disabled={isLoading}
          />
        </div>
        {form.formState.errors.vin && (
          <p className="text-red-500 text-xs font-['Heebo']">{form.formState.errors.vin.message?.toString()}</p>
        )}
      </div>

      {/* Mileage Input */}
      <div className="self-stretch flex flex-col gap-1">
        <div data-layer="Input Container" className={`InputContainer self-stretch ${inputHeight} ${inputPadding} bg-neutral-50 rounded-lg flex items-center overflow-hidden`}>
          <Input
            type="number"
            placeholder="Wprowadź przebieg"
            {...form.register('mileage')}
            className={`flex-1 justify-start text-zinc-700 ${fontSize} font-medium font-['Heebo'] border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-0 h-auto placeholder:text-zinc-700 placeholder:font-[500] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            disabled={isLoading}
          />
        </div>
        {form.formState.errors.mileage && (
          <p className="text-red-500 text-xs font-['Heebo']">{form.formState.errors.mileage.message?.toString()}</p>
        )}
      </div>

      {/* Transmission Select */}
      <div className="self-stretch flex flex-col gap-1">
        <div data-layer="Input Container" className={`InputContainer self-stretch ${inputHeight} ${inputPadding} bg-neutral-50 rounded-lg flex items-center overflow-hidden`}>
          <Select
            onValueChange={(value) => form.setValue('gearbox', value)}
            disabled={isLoading}
          >
            <SelectTrigger className={`TransmissionInput flex-1 justify-start text-zinc-700 ${fontSize} font-['Heebo'] border-none bg-transparent focus:ring-0 focus:ring-offset-0 h-auto p-0 font-[400] [&>span]:font-[400] [&>span]:font-['Heebo']`}>
              <SelectValue placeholder="Wybierz skrzynie biegów" className="font-[400]" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              <SelectItem value="manual">Manualna</SelectItem>
              <SelectItem value="automatic">Automatyczna</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.formState.errors.gearbox && (
          <p className="text-red-500 text-xs font-['Heebo']">{form.formState.errors.gearbox.message?.toString()}</p>
        )}
      </div>

      {/* Submit Button - with larger gap for "lower" positioning */}
      <div className={`self-stretch ${buttonMargin}`}>
        <div data-layer="Button Container" className={`ButtonContainer self-stretch ${inputPadding} ${buttonPadding} bg-[#D81B24] rounded-lg flex justify-center items-center overflow-hidden min-h-[44px]`}>
          <Button 
            type="submit" 
            className={`w-full justify-center text-neutral-50 ${buttonText} font-bold font-['Heebo'] bg-transparent hover:bg-transparent border-none p-0 h-auto`}
            disabled={isLoading || waitingForVerification}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Pobieranie wyceny...</span>
              </div>
            ) : waitingForVerification ? (
              "Czekam na weryfikację..."
            ) : (
              "Otrzymaj wycenę!"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ValuationInput;
